import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  ComposedChart, Line, CartesianGrid,
} from 'recharts';
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from 'react-leaflet';
import { getStats } from '../api';
import offlineCache from '../offlineCache';
import 'leaflet/dist/leaflet.css';

function FitBounds({ locations }) {
  const map = useMap();
  useEffect(() => {
    if (locations.length === 0) return;
    map.fitBounds(locations.map(l => [l.latitude, l.longitude]), { padding: [24, 24] });
  }, [locations, map]);
  return null;
}

function fmtTime(sec) {
  if (!sec) return '0u';
  const h = Math.floor(sec / 3600);
  const m = Math.round((sec % 3600) / 60);
  return `${h}u ${m}m`;
}
function fmtDuration(sec) {
  if (!sec) return '—';
  const h = Math.floor(sec / 3600);
  const m = Math.round((sec % 3600) / 60);
  return h > 0 ? `${h}u ${m}m` : `${m}m`;
}

// Maandnamen NL (kort)
const MAANDEN = ['jan','feb','mrt','apr','mei','jun','jul','aug','sep','okt','nov','dec'];
function fmtMonth(ym) {
  // ym = "2024-03"
  const [y, m] = ym.split('-');
  return `${MAANDEN[parseInt(m) - 1]} '${y.slice(2)}`;
}

export default function Dashboard() {
  const [stats, setStats]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [chartMode, setChartMode] = useState('maand'); // 'maand' | 'jaar'
  const navigate = useNavigate();

  useEffect(() => {
    getStats()
      .then(data => {
        offlineCache.set(offlineCache.KEYS.STATS, data);
        setStats(data);
      })
      .catch(() => {
        const cached = offlineCache.get(offlineCache.KEYS.STATS);
        if (cached) setStats(cached);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading">Even geduld…</div>;
  if (!stats)  return <div className="error">Kon statistieken niet laden.</div>;

  const { totals, byYear, byMonth, deepest, longest, locations = [],
          thisYear: cy, lastYear: py, monthComp = [] } = stats;

  // Huidig en vorig jaar
  const thisYearNum = new Date().getFullYear();
  const lastYearNum = thisYearNum - 1;

  // Maandvergelijking: 12 maanden, twee bars per maand
  const maandCompData = MAANDEN.map((name, idx) => {
    const m = idx + 1;
    const cyEntry = monthComp.find(r => r.year === thisYearNum && r.month === m);
    const pyEntry = monthComp.find(r => r.year === lastYearNum && r.month === m);
    return { label: name, huidig: cyEntry?.dives || 0, vorig: pyEntry?.dives || 0 };
  });

  // Maanddata: laatste 18 maanden, chronologisch (voor eventueel gebruik elders)
  const maandData = [...(byMonth || [])].reverse().slice(-18).map(r => ({
    label: fmtMonth(r.month),
    dives: r.dives,
  }));

  // Jaardata chronologisch met uren
  const jaarData = [...(byYear || [])].reverse().map(r => ({
    label: String(r.year),
    dives: r.dives,
    uren:  parseFloat(r.hours) || 0,
    diepte: parseFloat(r.max_depth) || 0,
  }));

  const mapCenter = locations.length > 0
    ? [locations[0].latitude, locations[0].longitude]
    : [50.85, 4.35];

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--text-muted)', marginBottom: 4 }}>
            JE OVERZICHT
          </div>
          <h1 className="page-title" style={{ margin: 0 }}>Dashboard</h1>
        </div>
        <div style={{ fontSize: 28 }}>🤿</div>
      </div>

      {/* Top stats */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 12, marginBottom: 16 }}>
        <div className="stat-card-featured">
          <div className="stat-label">Totale tijd in water</div>
          <div style={{ marginTop: 8 }}>
            <span className="stat-value">{fmtTime(totals.total_hours * 3600)}</span>
          </div>
          <div style={{ marginTop: 12, fontSize: 12, color: 'rgba(255,255,255,.65)' }}>
            verdeeld over {totals.years_diving} {totals.years_diving === 1 ? 'jaar' : 'jaar'}
          </div>
        </div>
        <div className="stat-card" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <div className="stat-label">Totaal duiken</div>
          <div className="stat-value">{totals.total_dives ?? 0}</div>
          <div style={{ marginTop: 8, height: 3, background: 'var(--blue-light)', borderRadius: 99 }}>
            <div style={{ width: '100%', height: '100%', background: 'var(--blue)', borderRadius: 99 }} />
          </div>
        </div>
      </div>

      {/* Stat rij */}
      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
        <div className="stat-card">
          <div className="stat-label">Diepste duik</div>
          <div className="stat-value">{totals.deepest_dive ?? 0}<span className="stat-unit">m</span></div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Gem. diepte</div>
          <div className="stat-value">{totals.avg_max_depth ?? 0}<span className="stat-unit">m</span></div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Koudste temp</div>
          <div className="stat-value">{totals.coldest_temp ?? '—'}<span className="stat-unit">°C</span></div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Warmste temp</div>
          <div className="stat-value">{totals.warmest_temp ?? '—'}<span className="stat-unit">°C</span></div>
        </div>
      </div>

      {/* Jaar vergelijking */}
      {(cy || py) && (
        <div className="card">
          <div className="card-title" style={{ marginBottom: 14 }}>📅 Dit jaar vs Vorig jaar</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {/* Duiken */}
            <div style={{ background: 'var(--surface)', borderRadius: 10, padding: '14px 16px' }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: .5, marginBottom: 6 }}>Duiken</div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <span style={{ fontSize: 28, fontWeight: 800, color: 'var(--blue)' }}>{cy?.dives ?? 0}</span>
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{thisYearNum}</span>
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                Vorig jaar: {py?.dives ?? 0}
              </div>
              {cy && py && (
                <div style={{
                  marginTop: 6, fontSize: 12, fontWeight: 700,
                  color: (cy.dives - py.dives) >= 0 ? '#16a34a' : '#dc2626',
                }}>
                  {(cy.dives - py.dives) >= 0 ? '+' : ''}{cy.dives - py.dives} duiken vs vorig jaar
                </div>
              )}
            </div>
            {/* Uren */}
            <div style={{ background: 'var(--surface)', borderRadius: 10, padding: '14px 16px' }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: .5, marginBottom: 6 }}>Uren</div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <span style={{ fontSize: 28, fontWeight: 800, color: 'var(--blue)' }}>{cy?.hours ?? 0}</span>
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>u {thisYearNum}</span>
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                Vorig jaar: {py?.hours ?? 0}u
              </div>
              {cy && py && (
                <div style={{
                  marginTop: 6, fontSize: 12, fontWeight: 700,
                  color: ((parseFloat(cy.hours) || 0) - (parseFloat(py.hours) || 0)) >= 0 ? '#16a34a' : '#dc2626',
                }}>
                  {((parseFloat(cy.hours) || 0) - (parseFloat(py.hours) || 0)) >= 0 ? '+' : ''}{(((parseFloat(cy.hours) || 0) - (parseFloat(py.hours) || 0))).toFixed(1)}u vs vorig jaar
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Activiteitsgrafiek */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div className="card-title" style={{ marginBottom: 0 }}>📊 Activiteit</div>
          <div style={{ display: 'flex', gap: 6 }}>
            {['maand', 'jaar'].map(m => (
              <button key={m} onClick={() => setChartMode(m)}
                style={{
                  padding: '4px 12px', borderRadius: 99, fontSize: 11, fontWeight: 700,
                  cursor: 'pointer', border: 'none',
                  background: chartMode === m ? 'var(--blue)' : 'var(--blue-light)',
                  color: chartMode === m ? '#fff' : 'var(--blue)',
                }}>
                {m === 'maand' ? 'Per maand' : 'Per jaar'}
              </button>
            ))}
          </div>
        </div>

        {chartMode === 'maand' ? (
          <>
            {/* Legenda */}
            <div style={{ display: 'flex', gap: 16, marginBottom: 10, fontSize: 11, color: 'var(--text-muted)' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <span style={{ width: 12, height: 12, borderRadius: 3, background: 'var(--blue)', display: 'inline-block' }} />
                {thisYearNum}
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <span style={{ width: 12, height: 12, borderRadius: 3, background: '#b0bec5', display: 'inline-block' }} />
                {lastYearNum}
              </span>
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={maandCompData} barCategoryGap="20%" barGap={2}>
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false} />
                <YAxis hide />
                <Tooltip
                  cursor={{ fill: 'var(--blue-light)' }}
                  contentStyle={{ borderRadius: 10, border: 'none', boxShadow: 'var(--shadow)', fontSize: 12 }}
                  formatter={(v, name) => [v, name === 'huidig' ? String(thisYearNum) : String(lastYearNum)]}
                />
                <Bar dataKey="vorig"  fill="#b0bec5" radius={[3, 3, 0, 0]} barSize={9} />
                <Bar dataKey="huidig" fill="var(--blue)" radius={[3, 3, 0, 0]} barSize={9} />
              </BarChart>
            </ResponsiveContainer>
          </>
        ) : (
          <ResponsiveContainer width="100%" height={200}>
            <ComposedChart data={jaarData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false} />
              <YAxis yAxisId="dives" hide />
              <YAxis yAxisId="uren" orientation="right" tick={{ fontSize: 10, fill: 'var(--text-muted)' }} tickFormatter={v => `${v}u`} />
              <Tooltip
                contentStyle={{ borderRadius: 10, border: 'none', boxShadow: 'var(--shadow)', fontSize: 12 }}
                formatter={(v, name) => name === 'dives' ? [v, 'Duiken'] : [`${v}u`, 'Uren']}
              />
              <Bar yAxisId="dives" dataKey="dives" fill="var(--blue)" radius={[6, 6, 0, 0]} barSize={28} />
              <Line yAxisId="uren" type="monotone" dataKey="uren" stroke="#f59e0b" strokeWidth={2} dot={{ fill: '#f59e0b', r: 4 }} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Kaart */}
      {locations.length > 0 && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '16px 20px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div className="card-title" style={{ marginBottom: 0 }}>🗺️ Je activiteitenkaart</div>
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{locations.length} locaties</span>
          </div>
          <div style={{ height: 220, position: 'relative' }}>
            <MapContainer center={mapCenter} zoom={6}
              style={{ height: '100%', width: '100%' }}
              zoomControl={false} dragging={false} scrollWheelZoom={false}
              doubleClickZoom={false} touchZoom={false} attributionControl={false}>
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              <FitBounds locations={locations} />
              {locations.map(l => (
                <CircleMarker key={l.id} center={[l.latitude, l.longitude]} radius={7}
                  pathOptions={{ color: '#fff', weight: 2, fillColor: '#0066ff', fillOpacity: 1 }}>
                  <Popup>
                    <div style={{ fontSize: 12, lineHeight: 1.6 }}>
                      <strong>{l.location_name || 'Onbekende locatie'}</strong><br />
                      {String(l.dive_date).substring(0,10)} · {l.max_depth_m}m
                    </div>
                  </Popup>
                </CircleMarker>
              ))}
            </MapContainer>
            <div onClick={() => navigate('/map')} style={{
              position: 'absolute', bottom: 12, right: 12, zIndex: 1000,
              background: '#fff', borderRadius: 8, padding: '6px 12px',
              fontSize: 11, fontWeight: 700, color: 'var(--blue)',
              boxShadow: 'var(--shadow)', cursor: 'pointer',
              textTransform: 'uppercase', letterSpacing: .5,
            }}>
              Volledige kaart ›
            </div>
          </div>
        </div>
      )}

      {/* Records */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        {deepest?.length > 0 && (
          <div className="card" style={{ marginBottom: 0 }}>
            <div className="card-title">🏆 Diepste duiken</div>
            {deepest.map((d, i) => (
              <div key={d.id} onClick={() => navigate(`/dives/${d.id}`)}
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: i < deepest.length - 1 ? '1px solid var(--border)' : 'none', cursor: 'pointer' }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13 }}>#{d.dive_number}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{String(d.dive_date).substring(0,10)}</div>
                </div>
                <div style={{ fontWeight: 800, fontSize: 16, color: 'var(--blue)' }}>{d.max_depth_m}m</div>
              </div>
            ))}
            <div className="see-all" onClick={() => navigate('/dives')}>NAAR ALLE DUIKEN <span>›</span></div>
          </div>
        )}
        {longest?.length > 0 && (
          <div className="card" style={{ marginBottom: 0 }}>
            <div className="card-title">⏱️ Langste duiken</div>
            {longest.map((d, i) => (
              <div key={d.id} onClick={() => navigate(`/dives/${d.id}`)}
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: i < longest.length - 1 ? '1px solid var(--border)' : 'none', cursor: 'pointer' }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13 }}>#{d.dive_number}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{String(d.dive_date).substring(0,10)}</div>
                </div>
                <div style={{ fontWeight: 800, fontSize: 16, color: 'var(--blue)' }}>{fmtDuration(d.duration_sec)}</div>
              </div>
            ))}
            <div className="see-all" onClick={() => navigate('/dives')}>NAAR ALLE DUIKEN <span>›</span></div>
          </div>
        )}
      </div>
    </div>
  );
}
